// src/pages/orders/Orders.js - Versión corregida y funcional
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Avatar,
  Button,
  Divider,
  Alert,
  Collapse,
  Stack,
  TableSortLabel
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  Euro as EuroIcon,
  Group as GroupIcon,
  FilterList as FilterListIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

// Paleta de colores verde pastel
const Colors = {
  primary: '#7FB069',
  primaryLight: '#A8D5BA',
  primaryDark: '#6B8E5A',
  secondary: '#000000',
  accent: '#90C695',
  background: '#F0F8F0',
  surface: '#FFFFFF',
  text: '#000000',
  textSecondary: '#724CA7',
  inactive: '#A0C4A7',
  white: '#FFFFFF',
  success: '#4CAF50',
  warning: '#FF9800',
  error: '#f44336',
  info: '#2196F3'
};

const Orders = () => {
  const navigate = useNavigate();
  
  // Estados principales
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  
  // Estados de filtrado y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Estados de paginación y ordenamiento
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderByField, setOrderByField] = useState('createdAt');
  const [orderDirection, setOrderDirection] = useState('desc');

  // Cargar pedidos al inicializar
  useEffect(() => {
    fetchOrders();
  }, []);

  // Aplicar filtros cuando cambien
  useEffect(() => {
    applyFilters();
  }, [searchTerm, statusFilter, dateFilter, orders, orderByField, orderDirection]);

  // Función para obtener pedidos
  const fetchOrders = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');
    
    try {
      const q = query(
        collection(db, 'orders'), 
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const ordersData = [];
      
      querySnapshot.forEach((doc) => {
        const orderData = doc.data();
        ordersData.push({ 
          id: doc.id, 
          ...orderData,
          total: orderData.total || 0,
          items: orderData.items || [],
          status: orderData.status || 'pending'
        });
      });
      
      setOrders(ordersData);
      setFilteredOrders(ordersData);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Error al cargar los pedidos. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Función para refrescar datos
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOrders(false);
  };

  // Aplicar filtros y ordenamiento
  const applyFilters = () => {
    let filtered = [...orders];
    
    // Filtro por término de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.id.toLowerCase().includes(term) ||
        (order.userEmail || '').toLowerCase().includes(term) ||
        (order.userName || '').toLowerCase().includes(term)
      );
    }
    
    // Filtro por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Filtro por fecha
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
        default:
          break;
      }
      
      if (dateFilter !== 'all') {
        filtered = filtered.filter(order => {
          const orderDate = order.createdAt instanceof Timestamp ? 
            order.createdAt.toDate() : 
            new Date(order.createdAt?.seconds * 1000);
          return orderDate >= filterDate;
        });
      }
    }
    
    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      let aValue = a[orderByField];
      let bValue = b[orderByField];
      
      // Manejar fechas
      if (orderByField === 'createdAt') {
        aValue = a.createdAt instanceof Timestamp ? 
          a.createdAt.toDate() : 
          new Date(a.createdAt?.seconds * 1000);
        bValue = b.createdAt instanceof Timestamp ? 
          b.createdAt.toDate() : 
          new Date(b.createdAt?.seconds * 1000);
      }
      
      // Manejar números
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return orderDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Manejar strings y fechas
      if (aValue < bValue) {
        return orderDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return orderDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    setFilteredOrders(filtered);
    setPage(0);
  };

  // Estadísticas calculadas
  const statistics = useMemo(() => {
    const total = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + (order.total || 0), 0);
    const totalItems = filteredOrders.reduce((sum, order) => sum + (order.items?.length || 0), 0);
    const uniqueCustomers = new Set(filteredOrders.map(order => order.userEmail)).size;

    return {
      total,
      totalRevenue,
      totalItems,
      uniqueCustomers
    };
  }, [filteredOrders]);

  // Manejadores de eventos
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
  };

  const handleSort = (property) => {
    const isAsc = orderByField === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderByField(property);
  };

  const handleViewOrder = (id) => {
    navigate(`/admin/orders/${id}`);
  };

  const handleEditOrder = (id) => {
    navigate(`/admin/orders/${id}`);
  };

  // Funciones de utilidad
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Fecha no disponible';
    
    const date = timestamp instanceof Timestamp ? 
      timestamp.toDate() : 
      new Date(timestamp.seconds * 1000);
      
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: 'Pendiente',
      processing: 'En proceso',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return statusMap[status] || status || 'Desconocido';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      pending: 'warning',
      processing: 'info',
      shipped: 'primary',
      delivered: 'success',
      cancelled: 'error'
    };
    return colorMap[status] || 'default';
  };

  const exportToCSV = async () => {
    if (filteredOrders.length === 0) {
      setError('No hay pedidos que exportar con los filtros actuales');
      return;
    }

    setExporting(true);
    setError('');

    try {
      const escapeCsvField = (field) => {
        if (field === null || field === undefined) return '""';
        let str = String(field);
        // Prevent formula injection
        if (/^[=+\-@]/.test(str)) str = "'" + str;
        // Escape quotes and wrap
        return '"' + str.replace(/"/g, '""') + '"';
      };

      // Excel convierte tallas como 5/6 o 11/12 en fechas (05-jun, 11-dic).
      // La sintaxis ="5/6" le obliga a tratarlas como texto sin perder el valor.
      const escapeSizeField = (value) => {
        const str = String(value ?? '');
        if (!str) return '""';
        if (!/^\d+\s*[/\-.]\s*\d+$/.test(str)) return escapeCsvField(str);
        return '"=""' + str.replace(/"/g, '""') + '"""';
      };

      // Los pedidos solo guardan el userId de la familia, no a que hijo va cada
      // producto. Cargamos los ninos una vez y los agrupamos por parentId para
      // resolver cada familia sin repetir consultas.
      const childrenSnap = await getDocs(collection(db, 'children'));
      const childrenByParent = new Map();
      childrenSnap.forEach((childDoc) => {
        const child = childDoc.data();
        if (!child.parentId) return;
        const siblings = childrenByParent.get(child.parentId) || [];
        siblings.push(child);
        childrenByParent.set(child.parentId, siblings);
      });

      // Una fila por nino. Los pedidos no guardan a que hijo va cada producto,
      // asi que se reparten por orden: la 1a unidad al 1er hermano, la 2a al 2o.
      const rows = [];
      filteredOrders.forEach((order) => {
        const familyChildren = childrenByParent.get(order.userId) || [];

        // Desglosar las lineas en unidades sueltas para poder repartirlas
        const units = [];
        (order.items || []).forEach((item) => {
          const cantidad = Math.max(1, Number(item?.quantity) || 1);
          for (let i = 0; i < cantidad; i += 1) {
            units.push({
              nombre: item?.nombre || '',
              talla: item?.talla || '',
              color: item?.color || ''
            });
          }
        });
        if (units.length === 0) units.push({ nombre: '', talla: '', color: '' });

        units.forEach((unit, index) => {
          const child = familyChildren[index];
          rows.push([
            child ? `${child.nombre || ''} ${child.apellidos || ''}`.trim() : '',
            unit.talla,
            child?.curso || '',
            unit.nombre,
            unit.color,
            order.userName || '',
            order.id.substring(0, 8).toUpperCase(),
            formatDate(order.createdAt),
            getStatusText(order.status),
            order.userEmail || ''
          ]);
        });
      });

      // Ordenar por curso y luego por nino, que es como se reparten los pedidos
      rows.sort((a, b) => a[2].localeCompare(b[2]) || a[0].localeCompare(b[0]));

      const TALLA_COL = 1;
      const csvContent = [
        ['Niño', 'Talla', 'Curso', 'Producto', 'Color', 'Familia', 'Nº Pedido', 'Fecha', 'Estado', 'Email'],
        ...rows
      ]
        .map((row) => row.map((cell, i) => (i === TALLA_COL ? escapeSizeField(cell) : escapeCsvField(cell))).join(';'))
        .join('\r\n');

      const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `pedidos_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al exportar pedidos:', error);
      setError('No se pudieron exportar los pedidos. Inténtalo de nuevo.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: Colors.background, minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: Colors.text, fontWeight: 'bold' }}>
          Gestión de Pedidos
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={exportToCSV}
            disabled={exporting}
            sx={{ 
              borderColor: Colors.primary,
              color: Colors.primary,
              '&:hover': { 
                borderColor: Colors.primaryDark,
                bgcolor: 'rgba(127, 176, 105, 0.1)'
              }
            }}
          >
            {exporting ? 'Exportando...' : 'Exportar CSV'}
          </Button>
          <Button
            variant="contained"
            startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ 
              bgcolor: Colors.primary,
              '&:hover': { bgcolor: Colors.primaryDark }
            }}
          >
            Actualizar
          </Button>
        </Stack>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: Colors.surface, border: `2px solid ${Colors.primaryLight}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color={Colors.textSecondary} gutterBottom variant="body2">
                    Total Pedidos
                  </Typography>
                  <Typography variant="h4" sx={{ color: Colors.text, fontWeight: 'bold' }}>
                    {statistics.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: Colors.primary }}>
                  <ShoppingCartIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: Colors.surface, border: `2px solid ${Colors.primaryLight}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color={Colors.textSecondary} gutterBottom variant="body2">
                    Ingresos Totales
                  </Typography>
                  <Typography variant="h4" sx={{ color: Colors.text, fontWeight: 'bold' }}>
                    {statistics.totalRevenue.toFixed(2)}€
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: Colors.accent }}>
                  <EuroIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: Colors.surface, border: `2px solid ${Colors.primaryLight}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color={Colors.textSecondary} gutterBottom variant="body2">
                    Productos Vendidos
                  </Typography>
                  <Typography variant="h4" sx={{ color: Colors.text, fontWeight: 'bold' }}>
                    {statistics.totalItems}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: Colors.primaryDark }}>
                  <TrendingUpIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: Colors.surface, border: `2px solid ${Colors.primaryLight}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color={Colors.textSecondary} gutterBottom variant="body2">
                    Clientes Únicos
                  </Typography>
                  <Typography variant="h4" sx={{ color: Colors.text, fontWeight: 'bold' }}>
                    {statistics.uniqueCustomers}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: Colors.textSecondary }}>
                  <GroupIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros y Búsqueda */}
      <Paper sx={{ 
        mb: 2, 
        bgcolor: Colors.surface,
        border: `1px solid ${Colors.primaryLight}`,
        borderRadius: 2
      }}>
        <Box sx={{ p: 2 }}>
          {/* Barra de búsqueda principal */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              variant="outlined"
              placeholder="Buscar por nº pedido, cliente o email..."
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: Colors.primary },
                  '&.Mui-focused fieldset': { borderColor: Colors.primary }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: Colors.primary }} />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={showFilters ? <ArrowUpIcon /> : <ArrowDownIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ 
                borderColor: Colors.primary,
                color: Colors.primary,
                '&:hover': { 
                  borderColor: Colors.primaryDark,
                  bgcolor: 'rgba(127, 176, 105, 0.1)'
                }
              }}
            >
              <FilterListIcon sx={{ mr: 1 }} />
              Filtros
            </Button>
          </Box>

          {/* Filtros avanzados */}
          <Collapse in={showFilters}>
            <Divider sx={{ mb: 2, borderColor: Colors.primaryLight }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: Colors.textSecondary }}>Estado</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    label="Estado"
                    sx={{
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: Colors.primary },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: Colors.primary }
                    }}
                  >
                    <MenuItem value="all">Todos los estados</MenuItem>
                    <MenuItem value="pending">Pendiente</MenuItem>
                    <MenuItem value="processing">En proceso</MenuItem>
                    <MenuItem value="shipped">Enviado</MenuItem>
                    <MenuItem value="delivered">Entregado</MenuItem>
                    <MenuItem value="cancelled">Cancelado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: Colors.textSecondary }}>Período</InputLabel>
                  <Select
                    value={dateFilter}
                    onChange={handleDateFilterChange}
                    label="Período"
                    sx={{
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: Colors.primary },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: Colors.primary }
                    }}
                  >
                    <MenuItem value="all">Todos los períodos</MenuItem>
                    <MenuItem value="today">Hoy</MenuItem>
                    <MenuItem value="week">Última semana</MenuItem>
                    <MenuItem value="month">Último mes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color={Colors.textSecondary}>
                    {filteredOrders.length} de {orders.length} pedidos
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </Box>
      </Paper>

      {/* Tabla de Pedidos */}
      <Paper sx={{ 
        bgcolor: Colors.surface,
        border: `1px solid ${Colors.primaryLight}`,
        borderRadius: 2,
        overflow: 'hidden'
      }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'rgba(127, 176, 105, 0.1)' }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderByField === 'id'}
                    direction={orderByField === 'id' ? orderDirection : 'asc'}
                    onClick={() => handleSort('id')}
                    sx={{ color: Colors.text, fontWeight: 'bold' }}
                  >
                    Nº Pedido
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderByField === 'userEmail'}
                    direction={orderByField === 'userEmail' ? orderDirection : 'asc'}
                    onClick={() => handleSort('userEmail')}
                    sx={{ color: Colors.text, fontWeight: 'bold' }}
                  >
                    Cliente
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderByField === 'createdAt'}
                    direction={orderByField === 'createdAt' ? orderDirection : 'asc'}
                    onClick={() => handleSort('createdAt')}
                    sx={{ color: Colors.text, fontWeight: 'bold' }}
                  >
                    Fecha
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderByField === 'total'}
                    direction={orderByField === 'total' ? orderDirection : 'asc'}
                    onClick={() => handleSort('total')}
                    sx={{ color: Colors.text, fontWeight: 'bold' }}
                  >
                    Total
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: Colors.text, fontWeight: 'bold' }}>
                  Productos
                </TableCell>
                <TableCell sx={{ color: Colors.text, fontWeight: 'bold' }}>
                  Estado
                </TableCell>
                <TableCell align="right" sx={{ color: Colors.text, fontWeight: 'bold' }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 4 }}>
                      <CircularProgress size={40} sx={{ color: Colors.primary }} />
                      <Typography variant="body2" sx={{ mt: 2, color: Colors.textSecondary }}>
                        Cargando pedidos...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box sx={{ py: 4 }}>
                      <ShoppingCartIcon sx={{ fontSize: 48, color: Colors.inactive, mb: 2 }} />
                      <Typography variant="h6" color={Colors.textSecondary}>
                        No se encontraron pedidos
                      </Typography>
                      <Typography variant="body2" color={Colors.textSecondary}>
                        Prueba a modificar los filtros de búsqueda
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((order) => (
                    <TableRow 
                      key={order.id}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: 'rgba(168, 213, 186, 0.1)',
                          transition: 'background-color 0.2s'
                        },
                        borderBottom: `1px solid ${Colors.primaryLight}`
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={`#${order.id.substring(0, 6).toUpperCase()}`}
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(127, 176, 105, 0.1)',
                            color: Colors.primaryDark,
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: Colors.text }}>
                            {order.userName || 'Usuario desconocido'}
                          </Typography>
                          <Typography variant="caption" color={Colors.textSecondary}>
                            {order.userEmail || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarIcon sx={{ fontSize: 16, mr: 1, color: Colors.textSecondary }} />
                          <Typography variant="body2" color={Colors.text}>
                            {formatDate(order.createdAt)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: Colors.primaryDark 
                          }}
                        >
                          {order.total?.toFixed(2) || '0.00'} €
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.items?.length || 0}
                          size="small"
                          sx={{
                            bgcolor: Colors.accent,
                            color: Colors.white
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusText(order.status)} 
                          color={getStatusColor(order.status)}
                          size="small" 
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Ver detalles" arrow>
                            <IconButton 
                              onClick={() => handleViewOrder(order.id)}
                              size="small"
                              sx={{ 
                                color: Colors.primary,
                                '&:hover': { 
                                  bgcolor: 'rgba(127, 176, 105, 0.1)',
                                  color: Colors.primaryDark
                                }
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar pedido" arrow>
                            <IconButton 
                              onClick={() => handleEditOrder(order.id)}
                              size="small"
                              sx={{ 
                                color: Colors.accent,
                                '&:hover': { 
                                  bgcolor: 'rgba(144, 198, 149, 0.1)',
                                  color: Colors.primaryDark
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            borderTop: `1px solid ${Colors.primaryLight}`,
            bgcolor: 'rgba(168, 213, 186, 0.05)',
            '& .MuiTablePagination-toolbar': {
              color: Colors.text
            },
            '& .MuiTablePagination-selectIcon': {
              color: Colors.primary
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default Orders;